/**
 * COMPLÉTUDE DE LA PÉRIODE AFFICHÉE — SSOT front (DEV-1650 / DEV-1661).
 * ============================================================================
 *
 * INTENTION (décision de Mathieu, 08/09/2026, mot pour mot) :
 *
 * > « Il faut pas que l'outil devienne une bannière à messages. L'affichage des
 * > trous, c'est plutôt un indicateur sur la page des données brutes de la source /
 * > une manière de représenter la donnée à la date du trou avec une légende qu'un
 * > ensemble de messages sinon on va pas s'en sortir je pense... »
 *
 * Donc : un trou ne se raconte pas, il se MONTRE, à sa date, dans la donnée, avec
 * une légende bornée. Ce module est la seule source de vérité front de cette
 * représentation : la barre de complétude, la légende et (plus tard) les bandes
 * dessinées dans les graphes lisent tous les mêmes états et les mêmes couleurs.
 *
 * RÈGLE QUI PRIME SUR TOUT (Mathieu, 07/09) : une absence de donnée n'est JAMAIS un
 * 0. Un jour sans point est `null` + un état ; un jour à 0 mesuré reste MESURÉ. Ici
 * cela se traduit par `estMesure()` : seule une valeur numérique FINIE compte comme
 * une mesure, et `0` en est une.
 *
 * RÈGLE QUI PRIME ENSUITE (charte art. 3) : rien ne se déduit d'un champ absent. Un
 * `cause: null` ou `provenances: null` veut dire « fait NON LU », jamais « aucune
 * cause ». Un jour sans fait servi tombe donc en `manquant`, jamais en `enedis` ni
 * en `homeys`.
 */

import type {
    StatusCollectePdl,
    WorkflowEnedisVerdict,
    WorkflowEnedisStreamEnRetard,
} from "@types"
import {
    causeDuFluxEnClair,
    causeDuStatutEnClair,
    collecteInterrompue,
    nomCourtDuFlux,
    provenanceEnClair,
} from "@modules/data/utils/libellesEnedis"
import type { DirectionEnedis } from "@modules/data/services/sourceDataEnedis.service"

// ============================================================================
// LES ÉTATS — un mot chacun, jamais une phrase.
// ============================================================================

/**
 * Les états d'un jour de la période affichée. L'axe est « d'où vient le trou »,
 * c'est-à-dire l'axe `owner` déjà servi par le verdict — pas une taxonomie de
 * causes fines, qui rouvrirait la bannière à messages que la décision ferme.
 *
 * SIX DEPUIS LE LOT 6a. Les quatre premiers sont ceux du 09/09 ; les deux
 * nouveaux répondent chacun à une question qu'aucun des quatre ne savait poser :
 *  • `attendu`     — ce jour N'EST PAS ENCORE DÛ. Il n'est pas « manquant », il
 *    est plus récent que le délai promis sur le flux qui le porte ;
 *  • `interrompue` — la collecte est INTERROMPUE : droit de collecte tombé,
 *    ou compteur que le distributeur ne relève plus à distance (non
 *    téléopérable, 27/10). Aucun jour n'est plus en chemin : les peindre
 *    « Collecte » ou « Enedis » dirait un retard qui n'existe pas.
 */
export type EtatCompletude =
    | "mesure"
    | "attendu"
    | "homeys"
    | "enedis"
    | "interrompue"
    | "manquant"

export type DescripteurEtat = {
    cle: EtatCompletude
    /** UN mot pour la légende. Jamais une phrase (décision du 08/09). */
    mot: string
    /** Fond de la case du jour. */
    fond: string
    /** Trait de la case du jour (et couleur des hachures). */
    trait: string
    /**
     * Le fond, en hexadécimal NU. Même teinte que `fond`, sans le jeton CSS.
     *
     * Plotly dessine dans un `<svg>` qu'il peint lui-même : il ne résout pas les
     * `var(--p-*)` et rendrait une bande transparente. La bande peinte dans le
     * graphe (DEV-1508 lot B1) lit donc cette forme-ci, et c'est toujours la
     * MÊME teinte que celle de la barre de complétude : une seule décision de
     * couleur par état, deux formes d'écriture.
     */
    fondHex: string
    /** Le trait (et la couleur des hachures), en hexadécimal nu. Voir `fondHex`. */
    traitHex: string
    /** Canal visuel : la mesure est PLEINE, toute absence est HACHURÉE. */
    motif: "plein" | "hachures"
}

/**
 * Un état, écrit une seule fois : chaque teinte est donnée en hexadécimal, et les
 * deux formes servies (jeton CSS avec repli, hexadécimal nu) en découlent. Sans
 * cela, la bande du graphe et la barre de complétude porteraient deux littéraux
 * qu'un seul des deux serait mis à jour le jour où la palette bouge.
 */
function etat(params: {
    cle: EtatCompletude
    mot: string
    motif: "plein" | "hachures"
    fond: { jeton: string; hex: string }
    trait: { jeton: string; hex: string }
}): DescripteurEtat {
    return {
        cle: params.cle,
        mot: params.mot,
        motif: params.motif,
        fond: `var(${params.fond.jeton}, ${params.fond.hex})`,
        trait: `var(${params.trait.jeton}, ${params.trait.hex})`,
        fondHex: params.fond.hex,
        traitHex: params.trait.hex,
    }
}

/**
 * Palette : jetons PrimeVue du design system front (`--p-*`), avec une valeur de
 * repli en dur pour Storybook et les tests, où le thème n'est pas monté.
 *
 * Le choix des teintes suit celui déjà servi par la qualité dataV2 (DEV-595,
 * `components/data/quality/dataQuality.ts`) : vert = servi et sûr, ambre = amont,
 * violet = la collecte (notre côté), gris = inconnu. Rien de nouveau à apprendre
 * pour le client, et aucun nom de société : l'outil est aussi vendu en marque blanche.
 */
export const ETATS_COMPLETUDE: Record<EtatCompletude, DescripteurEtat> = {
    mesure: etat({
        cle: "mesure",
        mot: "Mesuré",
        fond: { jeton: "--p-green-500", hex: "#22c55e" },
        trait: { jeton: "--p-green-600", hex: "#16a34a" },
        motif: "plein",
    }),
    // ⚠️ Le MOT ne nomme jamais la société : l'outil est vendu en marque blanche
    // (décision de Mathieu du 09/09/2026, DEV-1508 lot 0). La CLÉ technique
    // `homeys` reste celle de l'axe `owner` du verdict ; seul le mot affiché change.
    // « Collecte » : le manque est du côté de la collecte (notre boucle de commande,
    // un incident de notre chaîne), sans nommer qui collecte.
    homeys: etat({
        cle: "homeys",
        mot: "Collecte",
        fond: { jeton: "--p-purple-100", hex: "#ede9fe" },
        trait: { jeton: "--p-purple-500", hex: "#a78bfa" },
        motif: "hachures",
    }),
    enedis: etat({
        cle: "enedis",
        mot: "Enedis",
        fond: { jeton: "--p-amber-100", hex: "#fef3c7" },
        trait: { jeton: "--p-amber-500", hex: "#f59e0b" },
        motif: "hachures",
    }),
    // ⚠️ CE JOUR N'ACCUSE PERSONNE (lot 6a, volet 2). Il n'est pas encore dû :
    // la teinte est franche mais froide, pour qu'on la distingue du gris de
    // l'inconnu sans qu'elle se lise comme une alerte.
    attendu: etat({
        cle: "attendu",
        mot: "Attendu",
        fond: { jeton: "--p-blue-50", hex: "#eff6ff" },
        trait: { jeton: "--p-blue-400", hex: "#60a5fa" },
        motif: "hachures",
    }),
    // LE MOT VIENT DE LA CARTE DU COMPTEUR (« Collecte interrompue »,
    // `tag.collecte.interrompue_*`, DEV-1620) : une seule façon de nommer ce
    // fait dans toute l'application.
    interrompue: etat({
        cle: "interrompue",
        mot: "Interrompue",
        fond: { jeton: "--p-rose-100", hex: "#ffe4e6" },
        trait: { jeton: "--p-rose-500", hex: "#f43f5e" },
        motif: "hachures",
    }),
    manquant: etat({
        cle: "manquant",
        mot: "Manquant",
        fond: { jeton: "--p-surface-100", hex: "#f1f5f9" },
        trait: { jeton: "--p-surface-400", hex: "#94a3b8" },
        motif: "hachures",
    }),
}

/** Ordre d'affichage de la légende : ce qui est servi d'abord, l'inconnu en dernier. */
export const ORDRE_LEGENDE: EtatCompletude[] = [
    "mesure",
    "attendu",
    "homeys",
    "enedis",
    "interrompue",
    "manquant",
]

/**
 * Priorité quand plusieurs plages couvrent le MÊME jour avec des états différents.
 *
 * `homeys` passe devant `enedis` : règle de Mathieu du 04/09 sur les messages
 * client, « incident = nous d'abord ». Quand notre côté est en cause pour ce jour,
 * c'est notre côté qui est nommé, même si l'amont l'est aussi.
 */
const PRIORITE: EtatDePlage[] = [
    // LA COLLECTE INTERROMPUE (droit tombé, ou compteur non téléopérable depuis
    // le 27/10) PASSE DEVANT TOUT : quand plus rien ne peut être collecté,
    // désigner un responsable de la collecte pour ce jour serait faux.
    "interrompue",
    "homeys",
    "enedis",
    "manquant",
]

// ============================================================================
// LES PLAGES — traduction des faits servis par le verdict
// ============================================================================

/**
 * Une plage d'absence datée, prête à être peinte. `debut`/`fin` sont des jours ISO
 * (`YYYY-MM-DD`) ; `null` = plage OUVERTE de ce côté (sur le passé, ou sur
 * aujourd'hui), jamais « pas de plage ».
 */
/**
 * L'état qu'une PLAGE peut porter. Ni `mesure` ni `attendu` n'en sont : le
 * premier vient d'un point servi, le second se déduit du seul délai promis et
 * du calendrier. Aucun fait servi ne les date en plages.
 */
export type EtatDePlage = Exclude<EtatCompletude, "mesure" | "attendu">

export type PlageAbsence = {
    debut: string | null
    fin: string | null
    etat: EtatDePlage
    /** Ce qui sera lu dans l'infobulle du jour. `null` = rien à dire de plus. */
    detail: string | null
    /**
     * LE FLUX QUE CETTE PLAGE CONCERNE, tel que le verdict le nomme (lot 5g),
     * ou `null` quand le fait ne nomme aucun flux.
     *
     * Il ne s'écrit JAMAIS à l'écran — c'est un nom de notre plomberie (E9) : il
     * sert uniquement à ranger la plage du côté du bon sens quand la page peint
     * deux bandes. Une plage qui ne nomme pas son flux vaut pour les deux : elle
     * parle du compteur, pas d'un sens.
     */
    stream?: string | null
}

/** Jour ISO d'une date servie (ISO complet ou jour nu). `null` si illisible. */
export function jourISO(valeur: string | null | undefined): string | null {
    if (typeof valeur !== "string") return null
    const jour = valeur.slice(0, 10)
    return /^\d{4}-\d{2}-\d{2}$/.test(jour) ? jour : null
}

/**
 * Le fait « trou » est-il RÉELLEMENT servi pour ce verdict ?
 *
 * Sert de garde d'affichage : sans fait servi, la page reste STRICTEMENT celle
 * d'avant (aucun indicateur, aucune légende). Les deux faits qui arment sont ceux
 * que des interrupteurs amont désarmés retiennent aujourd'hui :
 * `absence_instruite`/`trous_connus` (`ENEDIS_TROUS_CONNUS`) et `streams_en_retard`
 * (`ENEDIS_JUGE_SUR_DUS`).
 *
 * ⚠️ `verdict.incident` n'arme PAS à lui seul : il est déjà servi en production
 * (DEV-1625) et l'allumer changerait le rendu du parc sans décision. Il enrichit la
 * lecture quand un autre fait a déjà armé l'indicateur.
 */
export function unFaitDeTrouEstServi(
    verdict: WorkflowEnedisVerdict | null | undefined
): boolean {
    if (!verdict) return false
    return (
        !!verdict.absence_instruite ||
        !!verdict.trous_connus ||
        Array.isArray(verdict.streams_en_retard)
    )
}

/** L'état d'un flux dû en retard, d'après sa cause puis, à défaut, son owner. */
function etatDuStream(stream: WorkflowEnedisStreamEnRetard): EtatDePlage {
    switch (stream?.cause) {
        // Notre boucle de commande n'a pas repassé la demande (DEV-1661).
        case "commande_non_passee":
            return "homeys"
        // La demande est partie ; ce qui manque est en face.
        case "commande_en_vol":
        case "commande_sans_donnee":
        case "flux_non_depose":
            return "enedis"
        default:
            break
    }
    // `cause` NON LUE : on retombe sur l'owner, seul autre fait servi. Sans owner,
    // le jour reste « manquant » — on n'invente pas un responsable.
    if (stream?.owner === "homeys") return "homeys"
    if (stream?.owner === "enedis") return "enedis"
    return "manquant"
}

/**
 * LE DÉTAIL D'INFOBULLE D'UN FLUX, EN CLAIR (lot 5a, écart D5).
 *
 * Ce que le client lisait en prod le 15/09 : « soutirage:cdc · flux_non_depose ».
 * Deux clés de notre plomberie, un deux-points et un souligné, servis sur une
 * surface client (E9). Ce qu'il lit maintenant : « Courbe de charge · flux non
 * déposé ».
 *
 * La règle du repli est la seule qui tienne la marque blanche : un flux qu'on ne
 * sait pas nommer est TU, une cause qu'on ne sait pas nommer est TUE, et quand
 * les deux manquent l'infobulle se contente du mot de l'état. Jamais la clé.
 */
export function detailDuStream(
    stream: WorkflowEnedisStreamEnRetard
): string | null {
    const morceaux = [
        nomCourtDuFlux(stream?.stream),
        causeDuFluxEnClair(stream?.cause),
    ].filter((m): m is string => typeof m === "string" && m.length > 0)
    return morceaux.length > 0 ? morceaux.join(" · ") : null
}

/**
 * Le détail d'une absence instruite : le COMMENT du constat, en clair. Les
 * provenances inconnues de la table sont écartées ; aucune provenance lisible
 * laisse l'infobulle au mot de l'état seul.
 */
function detailDeLAbsence(provenances: unknown): string | null {
    if (!Array.isArray(provenances)) return null
    const clairs = provenances
        .map((p) => provenanceEnClair(typeof p === "string" ? p : null))
        .filter((p): p is string => p !== null)
    return clairs.length > 0 ? clairs.join(", ") : null
}

/**
 * Traduit un verdict en plages datées. Rend `[]` quand rien n'est servi : aucune
 * plage n'est jamais fabriquée à partir d'un champ absent.
 */
export function plagesDuVerdict(
    verdict: WorkflowEnedisVerdict | null | undefined
): PlageAbsence[] {
    if (!verdict) return []
    const plages: PlageAbsence[] = []

    // 1. L'absence INSTRUITE et le trou CONNU : même forme, même état amont.
    //    La provenance est le détail d'infobulle ; `provenances: null` = fait non
    //    lu, l'infobulle se tait plutôt que d'affirmer un mode de constat.
    for (const absence of [verdict.absence_instruite, verdict.trous_connus]) {
        if (!absence) continue
        const debut = jourISO(absence.depuis)
        const fin = jourISO(absence.jusqu_au)
        if (debut === null && fin === null) continue
        plages.push({
            debut,
            fin,
            etat: "enedis",
            // E9 : le COMMENT en clair, jamais la clé de provenance.
            detail: detailDeLAbsence(absence.provenances),
        })
    }

    // 2. Les flux DUS en retard (A11, DEV-1632/DEV-1661). `depuis` est la dernière
    //    réception : le trou court DEPUIS le lendemain, et reste ouvert sur
    //    aujourd'hui (`fin: null`).
    if (Array.isArray(verdict.streams_en_retard)) {
        for (const stream of verdict.streams_en_retard) {
            if (!stream) continue
            plages.push({
                debut: lendemain(jourISO(stream.depuis)),
                fin: null,
                etat: etatDuStream(stream),
                detail: detailDuStream(stream),
                stream: stream.stream ?? null,
            })
        }
    }

    // 3. L'incident INTERNE (DEV-1625) : il n'arme pas l'indicateur, mais quand un
    //    autre fait l'a armé, il nomme notre côté sur sa propre fenêtre.
    if (verdict.incident?.depuis) {
        plages.push({
            debut: jourISO(verdict.incident.depuis),
            fin: null,
            etat: "homeys",
            // La `sub_cause` d'un incident est un mot de notre exploitation. Le
            // mot de l'état (« Collecte ») dit déjà de quel côté est le défaut.
            detail: null,
        })
    }

    return plages
}

/**
 * LES PLAGES D'ABSENCE LUES DU STATUT DU COMPTEUR (lot 5a, écart D8).
 *
 * POURQUOI (défaut de conception, pas de codage) : `plagesDuVerdict` ne parle
 * que d'un verdict de workflow `state=run`. Hors de cette fenêtre — c'est-à-dire
 * sur l'immense majorité des compteurs, dont tous ceux qui vont bien et tous
 * ceux qui sont arrêtés depuis longtemps — le verdict est `null`, et TOUT jour
 * sans point tombait en « Manquant ». Un compteur arrêté depuis le 28/06
 * affichait soixante-douze jours « Manquant » là où la cause est connue et
 * servie : Enedis ne dépose plus.
 *
 * Le statut agrégé (`data_retrieval.status`) porte les mêmes faits, pour le
 * compteur entier, et il est déjà chargé par `/source/informations`.
 *
 * DEUX PLAGES, ET SEULEMENT DEUX :
 *  1. l'absence INSTRUITE, bornée, quand Enedis l'a écrite ;
 *  2. le retard courant, OUVERT depuis le lendemain de la dernière mesure, avec
 *     l'état de celui à qui il appartient d'agir.
 *
 * CE QUI N'EST JAMAIS FAIT : fabriquer une plage sur un compteur dit à jour
 * (`a_jour: true` rend `[]`), ou nommer un responsable que le statut ne sert
 * pas (`a_qui_dagir` `client` / `none` / absent : le jour reste « Manquant », et
 * c'est la vérité — nous ne savons pas de quel côté est le trou).
 */
export function plagesDuStatut(
    statut: StatusCollectePdl | null | undefined
): PlageAbsence[] {
    if (!statut) return []
    const plages: PlageAbsence[] = []

    const absence = statut.absence_instruite as
        | Record<string, unknown>
        | null
        | undefined
    if (absence) {
        const debut = jourISO(absence.depuis as string | undefined)
        const fin = jourISO(absence.jusqu_au as string | undefined)
        if (debut !== null || fin !== null) {
            plages.push({
                debut,
                fin,
                etat: "enedis",
                detail: detailDeLAbsence(absence.provenances),
            })
        }
    }

    // LE DROIT DE COLLECTE EST TOMBÉ (lot 6a, volet 1) : ces jours-là ne sont
    // ni de notre faute ni de celle du distributeur, et ils n'étaient pas dus.
    //
    // LE DÉFAUT CORRIGÉ, mesuré en prod le 21/09 sur le C2
    // `653f9d4c3c68b26ee445413d` (consentement révoqué) : `a_qui_dagir` vaut
    // `client`, qui n'a pas d'état de bande, donc AUCUNE plage n'était posée et
    // les 92 jours tombaient en « Manquant » — pendant que le retard courant
    // servi par le verdict de workflow les peignait « Collecte ». Deux façons
    // d'accuser à tort pour un compteur dont le droit de lecture est tombé.
    //
    // ÉLARGI LE 27/10 (décision de Mathieu) à toute collecte INTERROMPUE : le
    // compteur non téléopérable peint « Interrompue », pas « Enedis ». Le mot
    // est celui de la ligne unique du bloc Fraîcheur, du titre et de la carte
    // (« Collecte interrompue chez Enedis ») ; l'ambre « Enedis » se lit comme
    // un retard du distributeur, une mesure en chemin, et c'est ce que la ligne
    // vient de cesser de dire. Le responsable reste nommé par la ligne (« chez
    // Enedis ») et la cause par le détail de la plage.
    if (collecteInterrompue(statut)) {
        plages.push({
            debut: lendemain(jourISO(statut.derniere_mesure)),
            fin: null,
            etat: "interrompue",
            detail: causeDuStatutEnClair(statut.cause),
        })
        return plages
    }

    // Le retard COURANT. `a_jour` absent = fait non instruit : rien à peindre.
    if (statut.a_jour === false) {
        const etat = etatDeLActeur(statut.a_qui_dagir)
        if (etat !== null) {
            plages.push({
                debut: lendemain(jourISO(statut.derniere_mesure)),
                fin: null,
                etat,
                detail: causeDuStatutEnClair(statut.cause),
            })
        }
    }

    return plages
}

/**
 * LES PLAGES QUI CONCERNENT CE SENS (lot 5g).
 *
 * Le verdict nomme son flux (`courbe_injection`, `energie_injection`, …) : les
 * flux d'injection portent tous le mot dans leur nom. Une plage qui ne nomme
 * aucun flux parle du COMPTEUR et vaut donc pour les deux sens — la taire d'un
 * côté ferait passer un retard connu pour un jour « Manquant ».
 */
export function plagesDuSens(
    plages: PlageAbsence[],
    direction: DirectionEnedis
): PlageAbsence[] {
    return plages.filter((plage) => {
        const flux = plage.stream
        if (!flux) return true
        return estFluxDInjection(flux) === (direction === "injection")
    })
}

/** Vrai quand ce nom de flux servi est celui d'une injection. */
export function estFluxDInjection(flux: string): boolean {
    return /injection/i.test(flux)
}

/** L'état de bande d'un responsable servi ; `null` hors des deux qui en ont un. */
function etatDeLActeur(acteur: string | null | undefined): EtatDePlage | null {
    if (acteur === "homeys") return "homeys"
    if (acteur === "enedis") return "enedis"
    return null
}

/** Jour suivant un jour ISO (`null` reste `null` : une borne ouverte le reste). */
export function lendemain(jour: string | null): string | null {
    if (jour === null) return null
    const d = new Date(`${jour}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return null
    d.setUTCDate(d.getUTCDate() + 1)
    return d.toISOString().slice(0, 10)
}

/** Une plage couvre-t-elle ce jour ? Une borne `null` est ouverte de ce côté. */
export function plageCouvre(plage: PlageAbsence, jour: string): boolean {
    if (plage.debut !== null && jour < plage.debut) return false
    if (plage.fin !== null && jour > plage.fin) return false
    return true
}

/** L'état d'un jour NON MESURÉ, d'après les plages qui le couvrent. */
export function etatDuJour(
    jour: string,
    plages: PlageAbsence[]
): { etat: EtatDePlage; detail: string | null } {
    const couvrantes = plages.filter((p) => plageCouvre(p, jour))
    for (const etat of PRIORITE) {
        const trouvee = couvrantes.find((p) => p.etat === etat)
        if (trouvee) return { etat, detail: trouvee.detail }
    }
    return { etat: "manquant", detail: null }
}

// ============================================================================
// LA MESURE — ce qui compte comme un jour mesuré
// ============================================================================

/**
 * Une valeur servie est-elle une MESURE ?
 *
 * `0` en est une (un vrai zéro reste un zéro). `null`, `undefined`, `NaN`, une
 * chaîne, un booléen n'en sont pas. C'est ici que se tient la règle du 07/09 : rien
 * dans ce module ne peut transformer une absence en `0`, ni un `0` en absence.
 */
export function estMesure(valeur: unknown): boolean {
    return typeof valeur === "number" && Number.isFinite(valeur)
}

/** Résout un chemin pointé (`energie_elec_tout.sum`) dans une ligne servie. */
function valeurAuChemin(
    ligne: Record<string, unknown>,
    chemin: string
): unknown {
    let courant: unknown = ligne
    for (const segment of chemin.split(".")) {
        if (courant === null || typeof courant !== "object") return undefined
        courant = (courant as Record<string, unknown>)[segment]
    }
    return courant
}

/** Une feuille numérique finie existe-t-elle quelque part dans la ligne ? */
function contientUneMesure(valeur: unknown, profondeur = 0): boolean {
    if (estMesure(valeur)) return true
    if (profondeur > 4 || valeur === null || typeof valeur !== "object")
        return false
    return Object.values(valeur as Record<string, unknown>).some((v) =>
        contientUneMesure(v, profondeur + 1)
    )
}

/**
 * Une ligne servie par dapi porte-t-elle une mesure ?
 *
 * `variables` = les chemins réellement demandés (ceux des KPI de la page). Vide,
 * on retombe sur « une feuille numérique quelque part », ce qui reste vrai et ne
 * fabrique rien.
 */
export function ligneEstMesuree(
    ligne: Record<string, unknown> | null | undefined,
    variables: string[]
): boolean {
    if (!ligne) return false
    if (variables.length > 0) {
        return variables.some((chemin) =>
            estMesure(valeurAuChemin(ligne, chemin))
        )
    }
    return Object.entries(ligne).some(
        ([cle, valeur]) => cle !== "date" && contientUneMesure(valeur)
    )
}

// ============================================================================
// LA COMPLÉTUDE — n jours mesurés / n jours attendus, et les causes
// ============================================================================

export type JourComplet = {
    jour: string
    etat: EtatCompletude
    detail: string | null
}

export type Completude = {
    /**
     * Jours de la période affichée dont la donnée est DUE.
     *
     * ⚠️ CE N'EST PLUS FORCÉMENT LE NOMBRE DE JOURS DU CALENDRIER (lot 6a,
     * volet 2). Les jours plus récents que le délai promis sur le flux ne sont
     * pas encore dus : ils sont comptés à part (`pasEncoreDus`) et sortent de
     * ce dénominateur. Compter contre nous un jour que personne ne nous doit
     * encore empêcherait un compteur parfaitement sain d'atteindre 100 %.
     *
     * Sans délai servi, aucun jour ne sort : c'est le nombre de jours du
     * calendrier, exactement comme avant.
     */
    attendus: number
    /** Jours affichés mais PAS ENCORE DUS. `0` quand aucun délai n'est servi. */
    pasEncoreDus: number
    /** Jours portant au moins un point mesuré. */
    mesures: number
    /** Un élément par jour attendu, dans l'ordre chronologique. */
    jours: JourComplet[]
    /** Comptage par état (les quatre clés sont toujours présentes). */
    parEtat: Record<EtatCompletude, number>
}

/**
 * Garde-fou : au delà, on ne peint pas jour par jour (une période de plus de dix
 * ans n'est pas un écran de données brutes). L'indicateur se tait plutôt que de
 * dessiner dix mille cases.
 */
export const PLAFOND_JOURS = 3700

/** Suite des jours ISO de `debut` à `fin`, bornes incluses. */
export function joursDeLaPeriode(debut: string, fin: string): string[] {
    const jours: string[] = []
    const d = new Date(`${debut}T00:00:00Z`)
    const f = new Date(`${fin}T00:00:00Z`)
    if (Number.isNaN(d.getTime()) || Number.isNaN(f.getTime())) return jours
    while (d.getTime() <= f.getTime() && jours.length <= PLAFOND_JOURS) {
        jours.push(d.toISOString().slice(0, 10))
        d.setUTCDate(d.getUTCDate() + 1)
    }
    return jours
}

/**
 * Calcule la complétude de la période affichée.
 *
 * Les LIGNES sont celles que la page a DÉJÀ reçues (`GET /api/dapi/source/:id/data`,
 * appelée une fois par `useKPI` pour les indicateurs) : aucune requête
 * supplémentaire n'est faite pour cet indicateur.
 *
 * Rend `null` quand la période est illisible ou hors plafond : un indicateur muet
 * vaut mieux qu'un indicateur faux.
 */
export function completudeDeLaPeriode(params: {
    debut: string
    fin: string
    lignes: Array<Record<string, unknown>> | null | undefined
    variables?: string[]
    plages?: PlageAbsence[]
    /** Voir `completudeDeJoursMesures`. Absent = comportement d'avant. */
    dernierJourDu?: string | null
}): Completude | null {
    const variables = params.variables ?? []
    const mesures: string[] = []
    for (const ligne of params.lignes ?? []) {
        const jour = jourISO(ligne?.date as string | undefined)
        if (jour === null) continue
        if (ligneEstMesuree(ligne, variables)) mesures.push(jour)
    }
    return completudeDeJoursMesures({
        debut: params.debut,
        fin: params.fin,
        joursMesures: mesures,
        plages: params.plages,
        dernierJourDu: params.dernierJourDu,
    })
}

/**
 * La même complétude, à partir des JOURS déjà reconnus comme mesurés.
 *
 * Deux appelants, une seule règle : la page de données brutes part des lignes
 * qu'elle a reçues (`completudeDeLaPeriode`), la page « Données du compteur »
 * part des séries que son onglet a chargées. Ni l'une ni l'autre ne fait une
 * requête de plus, et toutes deux tombent dans le même comptage par état.
 *
 * Un jour cité comme mesuré l'est parce qu'un point FINI y a été servi : la
 * règle du 07/09 (une absence n'est jamais un 0) tient en amont, chez celui qui
 * construit cette liste.
 */
export function completudeDeJoursMesures(params: {
    debut: string
    fin: string
    joursMesures: Iterable<string>
    plages?: PlageAbsence[]
    /**
     * LE DERNIER JOUR DONT LA DONNÉE EST DUE (lot 6a, volet 2), lu du délai
     * PROMIS sur le flux de cette famille (`dernierJourDuDuFlux`).
     *
     * Absent ou `null` = aucune promesse chiffrée n'est servie, donc aucun
     * jugement de présence n'est fabriqué : tous les jours sont traités comme
     * avant. Aucun seuil du front n'entre jamais ici.
     */
    dernierJourDu?: string | null
}): Completude | null {
    const jours = joursDeLaPeriode(params.debut, params.fin)
    if (jours.length === 0 || jours.length > PLAFOND_JOURS) return null

    const plages = params.plages ?? []
    const mesures = new Set<string>()
    for (const jour of params.joursMesures) {
        const iso = jourISO(jour)
        if (iso !== null) mesures.add(iso)
    }

    const parEtat: Record<EtatCompletude, number> = {
        mesure: 0,
        attendu: 0,
        homeys: 0,
        enedis: 0,
        interrompue: 0,
        manquant: 0,
    }
    const dernierJourDu = jourISO(params.dernierJourDu)
    const detail: JourComplet[] = jours.map((jour) => {
        if (mesures.has(jour)) {
            parEtat.mesure += 1
            return { jour, etat: "mesure" as EtatCompletude, detail: null }
        }
        // CE JOUR N'EST PAS ENCORE DÛ (lot 6a, volet 2), et cela prime sur
        // toute plage : une plage de retard court depuis le lendemain de la
        // dernière mesure et reste ouverte sur aujourd'hui, elle couvre donc
        // aussi les jours que personne ne nous doit encore.
        if (dernierJourDu !== null && jour > dernierJourDu) {
            parEtat.attendu += 1
            return { jour, etat: "attendu" as EtatCompletude, detail: null }
        }
        const { etat, detail: pourquoi } = etatDuJour(jour, plages)
        parEtat[etat] += 1
        return { jour, etat, detail: pourquoi }
    })

    return {
        attendus: jours.length - parEtat.attendu,
        pasEncoreDus: parEtat.attendu,
        mesures: parEtat.mesure,
        jours: detail,
        parEtat,
    }
}

// ============================================================================
// LE RAPPEL DE COMPLÉTUDE D'UN ONGLET (lot 5d, écart G1)
// ============================================================================
//
// INTENTION : celui qui descend d'un onglet à l'autre ne remonte pas lire la
// bande. Sous chaque onglet, une ligne dit ce que CET onglet a reçu sur ce qu'on
// regarde : « Énergie journalière : 20 / 92 jours mesurés · 72 Enedis ».
//
// Un nombre, rien d'autre (v1.4 §2.2 pt 4) : ni badge d'alerte, ni pourcentage,
// ni bannière. Les mots sont ceux de la bande, à l'identique, et un état à zéro
// ne s'écrit pas — « 0 Manquant » serait du bruit à lire sur chaque onglet sain.

/**
 * La phrase du rappel : la grandeur comptée, les jours mesurés sur les jours
 * regardés, puis les états non mesurés réellement présents.
 */
export function texteDuRappel(libelle: string, completude: Completude): string {
    const morceaux = [
        `${libelle} : ${completude.mesures} / ${completude.attendus} jours mesurés`,
    ]
    for (const etat of ORDRE_LEGENDE) {
        if (etat === "mesure") continue
        const n = completude.parEtat[etat]
        if (n > 0) morceaux.push(`${n} ${ETATS_COMPLETUDE[etat].mot}`)
    }
    return morceaux.join(" · ")
}

/** Les états réellement présents, dans l'ordre de la légende (pour n'afficher qu'eux). */
export function etatsPresents(completude: Completude): EtatCompletude[] {
    return ORDRE_LEGENDE.filter((etat) => completude.parEtat[etat] > 0)
}

// ============================================================================
// LE RAPPEL DES DÉPASSEMENTS NE COMPTE PAS DE MANQUANTS (lot 5d4)
// ============================================================================
//
// DÉFAUT MESURÉ le 16/09 sur le C2 `6a8eebfa` : « Dépassements : 0 / 92 jours
// mesurés · 92 Manquant ». Les trois quarts de cette phrase sont faux, et le
// dernier quart est ambigu.
//
// Une durée de dépassement n'est pas une mesure du compteur : c'est un calcul
// sur la courbe. Son absence un jour donné veut dire l'une de deux choses, et
// la page ne peut pas les distinguer : ou bien rien n'a dépassé, ou bien rien
// n'a été calculé. Peindre « 92 Manquant » tranche cette ambiguïté dans le sens
// de la panne, et compter 0 sur 92 « jours mesurés » fait passer une absence
// pour une mesure nulle — ce qu'une absence n'est jamais.
//
// ⚠️ ET UN 0 SERVI EST UNE VALEUR (relevé le même jour sur le C4 réel
// `638f1080` : 92 jours servis, tous à 0 h). « Aucune valeur » y serait faux :
// le calcul a bien tourné, et il a trouvé zéro. C'est le nombre de jours qui
// PORTENT une durée que le rappel compte, pas le nombre de dépassements.

/**
 * Le rappel de l'onglet Dépassements. Jamais un manquant, jamais un « 0 / 92 ».
 *
 * `derniere` est la date française de la dernière durée que la famille détient,
 * ou `null` quand elle n'est pas servie : la phrase s'arrête alors sans elle.
 */
export function texteDuRappelDesDepassements(
    libelle: string,
    completude: Completude,
    derniere: string | null
): string {
    if (completude.mesures > 0) {
        return `${libelle} : ${completude.mesures} jours avec une durée servie sur la fenêtre`
    }
    const phrase = `${libelle} : aucune valeur sur la fenêtre`
    return derniere ? `${phrase} · dernière le ${derniere}` : phrase
}
